import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PenTool, RotateCcw, Loader2, Check, Trash2 } from "lucide-react";
import warrantyApi from "@/api/warrantyApi";
import { getDocumentLabel } from "@/lib/warrantyUtils";

const SignatureCaptureForm = ({
  claim,
  documentObj,
  onOpenChange,
  onDone,
}) => {
  const queryClient = useQueryClient();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [signerName, setSignerName] = useState(() => {
    return (
      claim?.customer?.customer_name ||
      claim?.customer?.full_name ||
      claim?.customer?.name ||
      ""
    );
  });

  const docTypeLabel = getDocumentLabel(documentObj);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX = e.clientX;
    let clientY = e.clientY;

    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (isDrawing) {
      e?.preventDefault();
      setIsDrawing(false);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const getCroppedSignatureDataUrl = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const imgData = ctx.getImageData(0, 0, width, height);
    const data = imgData.data;

    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    let found = false;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 0) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          found = true;
        }
      }
    }

    if (!found) return null;

    const padding = 10;
    minX = Math.max(0, minX - padding);
    minY = Math.max(0, minY - padding);
    maxX = Math.min(width, maxX + padding);
    maxY = Math.min(height, maxY + padding);

    const croppedWidth = maxX - minX;
    const croppedHeight = maxY - minY;

    const croppedCanvas = document.createElement("canvas");
    croppedCanvas.width = croppedWidth;
    croppedCanvas.height = croppedHeight;
    const croppedCtx = croppedCanvas.getContext("2d");
    croppedCtx.drawImage(
      canvas,
      minX,
      minY,
      croppedWidth,
      croppedHeight,
      0,
      0,
      croppedWidth,
      croppedHeight,
    );

    return croppedCanvas.toDataURL("image/png");
  };

  const signMutation = useMutation({
    mutationFn: async () => {
      const croppedDataUrl = getCroppedSignatureDataUrl();
      if (!croppedDataUrl) {
        throw new Error("Please draw a signature before submitting");
      }

      const docId = documentObj?.id;
      if (!docId) {
        throw new Error("Document ID not found");
      }

      const payload = {
        signed_by_name: signerName.trim() || undefined,
        signature: croppedDataUrl,
      };

      const res = await warrantyApi.signDocument(claim.id, docId, payload);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success("Document signed successfully");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }
      onOpenChange(false);
      if (onDone) onDone(data);
    },
    onError: (error) => {
      const msg = error.response?.data?.message || error.message || "Failed to sign document";
      toast.error(msg);
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }
    },
  });

  const removeSigMutation = useMutation({
    mutationFn: () => warrantyApi.removeSignature(claim.id, documentObj.id),
    onSuccess: () => {
      toast.success(
        "Signature removed successfully. Document is ready for physical signing.",
      );
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({
          queryKey: ["warrantyClaim", String(claim.id)],
        });
      }
      onOpenChange(false);
      if (onDone) onDone();
    },
    onError: (error) => {
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to remove signature";
      toast.error(msg);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hasDrawn) {
      toast.error("Please provide a signature on the pad");
      return;
    }
    signMutation.mutate();
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <PenTool className="h-5 w-5" />
          </div>
          <div>
            <DialogTitle>Capture Signature</DialogTitle>
            <DialogDescription>
              Sign <span className="font-medium capitalize">{docTypeLabel}</span> for claim #{claim?.claim_number}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4 py-2">
        <div className="space-y-2">
          <Label htmlFor="signer_name">Signer Name *</Label>
          <Input
            id="signer_name"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            placeholder="e.g. John Doe (Customer or Authorized Rep)"
            required
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Signature Pad *</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
              onClick={clearCanvas}
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
              Clear
            </Button>
          </div>

          <div className="border rounded-lg bg-white overflow-hidden shadow-inner touch-none relative">
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              className="w-full h-[180px] cursor-crosshair block bg-white"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            {!hasDrawn && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-muted-foreground/40 text-xs">
                Sign above with finger, stylus, or mouse
              </div>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Canvas will be automatically cropped and compressed before submission.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2 flex flex-col sm:flex-row sm:justify-between items-stretch sm:items-center">
          {documentObj?.is_signed ? (
            <Button
              type="button"
              variant="outline"
              className="text-destructive border-destructive/30 hover:bg-destructive/10 text-xs gap-1.5"
              onClick={() => removeSigMutation.mutate()}
              disabled={removeSigMutation.isPending || signMutation.isPending}
            >
              {removeSigMutation.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Removing Signature...
                </>
              ) : (
                <>
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove Signature (Sign Physically)
                </>
              )}
            </Button>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={signMutation.isPending || removeSigMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={signMutation.isPending || removeSigMutation.isPending || !hasDrawn}
            >
              {signMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving Signature...
                </>
              ) : (
                <>
                  <Check className="mr-1.5 h-4 w-4" />
                  Apply Signature
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </form>
    </>
  );
};

SignatureCaptureForm.propTypes = {
  claim: PropTypes.object.isRequired,
  documentObj: PropTypes.object,
  onOpenChange: PropTypes.func.isRequired,
  onDone: PropTypes.func,
};

const SignatureCaptureModal = ({
  open,
  onOpenChange,
  claim,
  documentObj,
  onDone,
}) => {
  if (!claim) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        {open && (
          <SignatureCaptureForm
            key={documentObj?.id || "sign"}
            claim={claim}
            documentObj={documentObj}
            onOpenChange={onOpenChange}
            onDone={onDone}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

SignatureCaptureModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  claim: PropTypes.object,
  documentObj: PropTypes.object,
  onDone: PropTypes.func,
};

export default SignatureCaptureModal;
